import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { logout } from "../redux/authSlice";
import { motion } from "framer-motion";
import {
  User, Lock, Shield, Globe, Bell, CreditCard, Wallet,
  BarChart2, Settings, LogOut, ChevronLeft, ChevronRight,
  ShieldCheck, HelpCircle, Info, FileText, Landmark,
  Receipt, BadgePercent, Building2, ExternalLink,
} from "lucide-react";

const FONT_CSS = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');`;

const T = {
  bg: "#ffffff", surface: "#fafafa", surface2: "#f5f5f0",
  border: "#e5e5e5", border2: "#d4d4d4",
  text: "#0a0a0a", textSoft: "#525252", muted: "#a3a3a3",
  accent: "#0a0a0a", danger: "#b91c1c",
  serif: "'Cormorant Garamond', serif",
  sans: "'Montserrat', sans-serif",
};

const ProfileSettings: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = user?.userId;

  const handleLogout = async () => {
    await dispatch(logout());
    navigate("/AuthSwitch");
  };

  /* ── LOGGED OUT — redirect or show login prompt ── */
  if (!user) {
    return (
      <div style={{ minHeight:"100vh", background:"#000", fontFamily:T.sans }}>
        <style>{FONT_CSS}</style>
        <header style={S.header}>
          <div style={S.headerLeft} onClick={() => navigate(-1)}><ChevronLeft size={20} color="#e8e8e8"/><span style={S.backLabel}>Back</span></div>
          <span style={S.headerBrand}>EBuOS</span>
          <div style={{ width:60 }}/>
        </header>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:"80vh", padding:24, textAlign:"center" }}>
          <h2 style={{ fontSize:28, fontWeight:300, color:"#fff", fontFamily:T.serif, letterSpacing:"0.06em", marginBottom:10 }}>Sign in to EBuOS</h2>
          <p style={{ fontSize:13, color:"#6b6b6b", marginBottom:28 }}>Log in to manage your account settings</p>
          <button onClick={() => navigate("/AuthSwitch")} style={{ background:"transparent", border:"1px solid #fff", color:"#fff", borderRadius:0, padding:"14px 48px", fontSize:12, fontWeight:500, letterSpacing:"0.2em", cursor:"pointer", fontFamily:T.sans }}>LOGIN</button>
        </div>
      </div>
    );
  }

  const ini = ((user.firstName?.[0]||"") + (user.lastName?.[0]||"")).toUpperCase() || "U";

  /* ── Settings groups ── */
  const accountItems = [
    { label:"Personal Information", sub:"Name, email & profile", icon:<User size={17}/>, path:`/editProfile/${userId}` },
    { label:"Login & Security", sub:"Password & authentication", icon:<Lock size={17}/>, path:`/loginsecurity/${userId}` },
    { label:"Privacy", sub:"Data sharing & visibility", icon:<Shield size={17}/>, path:"/privacy" },
    { label:"Notifications", sub:"Alerts & communication", icon:<Bell size={17}/>, path:"/Notifications" },
    { label:"Languages & Currency", sub:"Region & display preferences", icon:<Globe size={17}/>, path:`/CurrencySelection/${userId}` },
  ];

  const financeItems = [
    { label:"Wallet & Cards", sub:"Balances, cards & payment methods", icon:<Wallet size={17}/>, path:"/PaymentMethodDashboard" },
    { label:"Transactions", sub:"Full payment history", icon:<Receipt size={17}/>, path:"/transactions" },
    { label:"Billing Address", sub:"Invoice & billing details", icon:<Building2 size={17}/>, path:`/editProfile/${userId}` },
    { label:"Bank Accounts", sub:"IBAN, BIC & linked banks", icon:<Landmark size={17}/>, path:`/editProfile/${userId}` },
    { label:"Tax Information", sub:"Tax ID, VAT & compliance", icon:<BadgePercent size={17}/>, path:`/editProfile/${userId}` },
    { label:"Subscription & Plans", sub:"Your current plan & upgrades", icon:<CreditCard size={17}/>, path:"/subscription" },
  ];

  const moreItems = [
    { label:"Account Settings", sub:"Full account configuration", icon:<Settings size={17}/>, path:`/AccountSettings/${userId}` },
    { label:"Verify Profile", sub:"Complete identity verification", icon:<ShieldCheck size={17}/>, path:"/VerifyProfile" },
    { label:"About EBuOS", sub:"Learn more about us", icon:<Info size={17}/>, path:"/pages/about-us" },
    { label:"Help & Support", sub:"Get assistance", icon:<HelpCircle size={17}/>, path:"/pages/help-support" },
    { label:"Terms of Service", sub:"Legal agreements", icon:<FileText size={17}/>, path:"/terms" },
  ];

  return (
    <div style={S.root}>
      <style>{FONT_CSS}</style>

      {/* ── Header ── */}
      <header style={S.header}>
        <div style={S.headerLeft} onClick={() => navigate(-1)}><ChevronLeft size={20} color="#e8e8e8"/><span style={S.backLabel}>Back</span></div>
        <span style={S.headerBrand}>EBuOS</span>
        <div style={{ width:60 }}/>
      </header>

      <div style={S.content}>

        {/* ── Profile card ── */}
        <div style={S.profileCard}>
          <div style={S.avatar}>
            {user.profilePicture
              ? <img src={user.profilePicture} alt="" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:"50%" }}/>
              : <span style={S.avatarIni}>{ini}</span>
            }
          </div>
          <div style={{ flex:1 }}>
            <div style={S.profileName}>{user.firstName} {user.lastName}</div>
            <div style={S.profileUser}>@{user.username || "username"}</div>
            <div style={{ display:"inline-flex", alignItems:"center", gap:4, marginTop:6, padding:"3px 10px", border:"1px solid "+(user.isVerified?T.text:T.border), background:user.isVerified?"rgba(0,0,0,0.04)":"transparent" }}>
              {user.isVerified ? <ShieldCheck size={10} color={T.text}/> : <Shield size={10} color={T.muted}/>}
              <span style={{ fontSize:9, fontWeight:500, letterSpacing:"0.12em", color:user.isVerified?T.text:T.muted }}>{user.isVerified?"VERIFIED":"UNVERIFIED"}</span>
            </div>
          </div>
          <button onClick={() => navigate(`/editProfile/${userId}`)} style={S.editProfileBtn}>
            EDIT PROFILE
          </button>
        </div>

        {/* ── Account ── */}
        <SectionHead label="ACCOUNT"/>
        {accountItems.map((it,i) => <SettingsRow key={it.label} item={it} index={i} navigate={navigate}/>)}

        {/* ── Finance ── */}
        <SectionHead label="FINANCE & PAYMENTS"/>
        {financeItems.map((it,i) => <SettingsRow key={it.label} item={it} index={i} navigate={navigate}/>)}

        {/* ── More ── */}
        <SectionHead label="MORE"/>
        {moreItems.map((it,i) => <SettingsRow key={it.label} item={it} index={i} navigate={navigate}/>)}

        {/* ── Logout ── */}
        <div style={{ marginTop:32, paddingTop:24, borderTop:"1px solid "+T.border }}>
          <motion.button
            whileTap={{ scale:0.97 }}
            onClick={handleLogout}
            style={S.logoutBtn}>
            <LogOut size={16} style={{ marginRight:8 }}/> LOG OUT
          </motion.button>
        </div>

      </div>
    </div>
  );
};

export default ProfileSettings;

/* ── Sub-components ── */
const SectionHead: React.FC<{label:string}> = ({label}) => (
  <div style={{ marginTop:32, marginBottom:12, paddingBottom:10, borderBottom:"1px solid "+T.border }}>
    <span style={{ fontSize:10, color:T.muted, letterSpacing:"0.2em", fontWeight:500, fontFamily:T.sans }}>{label}</span>
  </div>
);

const SettingsRow: React.FC<{item:{label:string;sub:string;icon:React.ReactNode;path:string};index:number;navigate:(p:string)=>void}> = ({item,index,navigate}) => (
  <motion.button
    initial={{ opacity:0, y:10 }}
    animate={{ opacity:1, y:0 }}
    transition={{ delay:index*0.04 }}
    whileHover={{ x:4 } as any}
    onClick={() => navigate(item.path)}
    style={S.row}>
    <div style={S.iconWrap}>{item.icon}</div>
    <div style={{ flex:1, textAlign:"left" }}>
      <div style={S.rowLabel}>{item.label}</div>
      <div style={S.rowSub}>{item.sub}</div>
    </div>
    <ChevronRight size={16} color={T.muted}/>
  </motion.button>
);

/* ── Styles ── */
const S: { [key: string]: React.CSSProperties } = {
  root: { minHeight:"100vh", background:T.bg, fontFamily:T.sans },

  header: {
    position:"sticky", top:0, zIndex:50,
    background:"rgba(0,0,0,0.95)", backdropFilter:"blur(12px)",
    borderBottom:"1px solid #1c1c1c",
    display:"flex", alignItems:"center", justifyContent:"space-between",
    padding:"0 20px", height:56,
  },
  headerLeft: { display:"flex", alignItems:"center", gap:6, cursor:"pointer" },
  backLabel: { color:"#e8e8e8", fontSize:12, fontWeight:400, letterSpacing:"0.05em", fontFamily:T.sans },
  headerBrand: { position:"absolute" as const, left:"50%", transform:"translateX(-50%)", color:"#fff", fontWeight:300, letterSpacing:"0.3em", fontSize:14, fontFamily:T.serif },

  content: { maxWidth:620, margin:"0 auto", padding:"28px 20px 80px" },

  /* Profile card */
  profileCard: {
    display:"flex", alignItems:"center", gap:16,
    padding:"24px 0", borderBottom:"1px solid "+T.border,
    flexWrap:"wrap" as const,
  },
  avatar: {
    width:64, height:64, borderRadius:"50%",
    background:T.surface2, border:"1px solid "+T.border2,
    display:"flex", alignItems:"center", justifyContent:"center",
    overflow:"hidden", flexShrink:0,
  },
  avatarIni: { fontSize:22, fontWeight:300, color:T.text, fontFamily:T.serif },
  profileName: { fontSize:20, fontWeight:400, color:T.text, fontFamily:T.serif, letterSpacing:"0.03em" },
  profileUser: { fontSize:12, color:T.textSoft, marginTop:2 },
  editProfileBtn: {
    background:T.accent, color:T.bg, border:"none", borderRadius:0,
    padding:"10px 22px", fontSize:10, fontWeight:500, letterSpacing:"0.18em",
    cursor:"pointer", fontFamily:T.sans, flexShrink:0,
  },

  /* Rows */
  row: {
    display:"flex", alignItems:"center", gap:14,
    padding:"16px 4px", borderBottom:"1px solid "+T.border,
    background:"none", border:"none", borderBottomStyle:"solid" as const,
    borderBottomWidth:1, borderBottomColor:T.border,
    cursor:"pointer", width:"100%", textAlign:"left" as const,
    fontFamily:"inherit", transition:"background 0.15s",
  },
  iconWrap: {
    width:40, height:40, borderRadius:0,
    background:"rgba(0,0,0,0.03)", border:"1px solid "+T.border,
    display:"flex", alignItems:"center", justifyContent:"center",
    flexShrink:0, color:T.text,
  },
  rowLabel: { fontSize:14, fontWeight:500, color:T.text, letterSpacing:"0.01em" },
  rowSub: { fontSize:11, color:T.muted, marginTop:2 },

  /* Logout */
  logoutBtn: {
    width:"100%", background:"transparent",
    border:"1px solid "+T.border2, borderRadius:0,
    color:T.textSoft, fontWeight:500, padding:"14px",
    cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center",
    fontSize:11, letterSpacing:"0.18em", fontFamily:T.sans,
  },
};